import { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';

type DuelWinLossRecordProps = {
  userId: string | null;
  variant?: 'compact' | 'default' | 'detailed';
};

type DuelStats = {
  wins: number;
  losses: number;
  total: number;
};

const isMissingRelation = (error: any) => {
  const msg = String(error?.message ?? '');
  return error?.code === '42P01' || /does not exist/i.test(msg);
};

export function DuelWinLossRecord({ userId, variant = 'default' }: DuelWinLossRecordProps) {
  const { theme } = useTheme();
  const [stats, setStats] = useState<DuelStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [missingView, setMissingView] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!userId) {
        setStats(null);
        setMissingView(false);
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('public_user_duel_stats_v1')
          .select('user_id, wins, losses, total')
          .eq('user_id', userId)
          .maybeSingle();

        // Always fall back to direct duels query for accuracy
        // (view may exist but be stale or empty for new users)
        const [{ count: directWins }, { count: directTotal }] = await Promise.all([
          supabase
            .from('duels')
            .select('id', { count: 'exact', head: true })
            .eq('winner_user_id', userId)
            .in('status', ['completed', 'resolved']),
          supabase
            .from('duels')
            .select('id', { count: 'exact', head: true })
            .or(`challenger_id.eq.${userId},challenged_id.eq.${userId}`)
            .in('status', ['completed', 'resolved']),
        ]);

        const w = directWins ?? 0;
        const t = directTotal ?? 0;

        // If view had data and matches, use view; otherwise use direct query
        const viewWins = error ? 0 : Number(data?.wins ?? 0);
        const viewTotal = error ? 0 : Number(data?.total ?? 0);
        const useView = !error && data && viewTotal > 0;

        if (active) {
          setMissingView(false);
          setStats({
            wins: useView ? viewWins : w,
            losses: useView ? Number(data?.losses ?? 0) : (t - w),
            total: useView ? viewTotal : t,
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [userId]);

  if (loading) {
    return (
      <Text style={[styles.text, { color: theme.colors.textSecondary }]}>Loading...</Text>
    );
  }

  if (!userId || missingView) {
    return (
      <Text style={[styles.text, { color: theme.colors.textSecondary }]}>—</Text>
    );
  }

  const wins = stats?.wins ?? 0;
  const losses = stats?.losses ?? 0;

  if (variant === 'compact') {
    return (
      <Text style={[styles.compactText, { color: theme.colors.text }]}>
        {Number(wins)}W-{Number(losses)}L
      </Text>
    );
  }

  return (
    <View>
      <Text style={[styles.text, { color: theme.colors.textSecondary }]}>
        Wins {Number(wins)} • Losses {Number(losses)}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  text: {
    fontSize: 14,
    fontWeight: '600',
  },
  compactText: {
    fontSize: 18,
    fontFamily: 'Oswald_700Bold',
    fontVariant: ['tabular-nums'],
  },
});
